"use client";

import { useEffect, useState } from "react";
import { HOMEPAGE_PRIMARY_BUTTON_CLASS } from "@/components/landing/homepage-button-styles";

const hiddenStorageKey = "feedvote-support-hidden";
type SupportChatComponent =
  typeof import("./support-chat-widget")["SupportChatWidget"];
let supportChatModulePromise: Promise<
  typeof import("./support-chat-widget")
> | null = null;

function loadSupportChatModule() {
  supportChatModulePromise ??= import("./support-chat-widget");
  return supportChatModulePromise;
}

type ChatLauncherProps = {
  isLoading?: boolean;
  onHide?: () => void;
  onOpen?: () => void;
};

function ChatLauncher({
  isLoading = false,
  onHide,
  onOpen,
}: ChatLauncherProps) {
  return (
    <div className="group/launcher fixed right-5 bottom-5 z-[80] sm:right-6 sm:bottom-6">
      {onHide ? (
        <button
          type="button"
          aria-label="Hide support chat permanently"
          title="Hide support chat"
          onClick={onHide}
          className="-top-1 -right-1 absolute z-10 flex size-6 scale-90 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 opacity-0 shadow-lg shadow-slate-950/10 transition hover:bg-slate-50 hover:text-slate-900 focus-visible:scale-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-indigo-100 group-hover/launcher:scale-100 group-hover/launcher:opacity-100"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            className="size-3.5"
          >
            <path d="m6 6 12 12M18 6 6 18" />
          </svg>
        </button>
      ) : null}

      <button
        type="button"
        aria-label={isLoading ? "Loading support chat" : "Open support chat"}
        title={isLoading ? "Loading support chat" : "Open support chat"}
        disabled={isLoading}
        onClick={onOpen}
        className={`${HOMEPAGE_PRIMARY_BUTTON_CLASS} min-h-12 gap-2 px-4 py-2.5 text-sm shadow-lg shadow-indigo-950/20 disabled:cursor-wait motion-reduce:transform-none`}
      >
        {isLoading ? (
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            className="size-4 animate-spin motion-reduce:animate-none"
          >
            <path d="M21 12a9 9 0 1 1-6.2-8.6" />
          </svg>
        ) : (
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="size-4"
          >
            <path d="M14 9a2 2 0 0 1-2 2H6l-4 4V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2Z" />
            <path d="M16 5h4a2 2 0 0 1 2 2v10l-4-3h-6a2 2 0 0 1-2-2v-1" />
          </svg>
        )}
        <span>{isLoading ? "Opening…" : "Chat with us"}</span>
      </button>
    </div>
  );
}

export function LazySupportChatWidget() {
  const [isReady, setIsReady] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [SupportChatWidget, setSupportChatWidget] =
    useState<SupportChatComponent | null>(null);

  useEffect(() => {
    setIsHidden(window.localStorage.getItem(hiddenStorageKey) === "true");
    setIsReady(true);
  }, []);

  if (!isReady || isHidden) {
    return null;
  }

  if (SupportChatWidget) {
    return <SupportChatWidget surface="landing" initiallyOpen />;
  }

  return (
    <ChatLauncher
      isLoading={isLoading}
      onOpen={() => {
        setIsLoading(true);
        void loadSupportChatModule()
          .then((module) => {
            setSupportChatWidget(() => module.SupportChatWidget);
          })
          .catch(() => {
            supportChatModulePromise = null;
          })
          .finally(() => setIsLoading(false));
      }}
      onHide={() => {
        window.localStorage.setItem(hiddenStorageKey, "true");
        setIsHidden(true);
      }}
    />
  );
}
